import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { ChevronLeft, User, Calendar, Mail, CheckCircle, XCircle } from 'lucide-react';
import './AdminPages.css';

function AdminUserDetail() {
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('goals'); // goals, plans, planExecutions, testExecutions

    useEffect(() => {
        loadUserDetail();
    }, [id]);

    const loadUserDetail = async () => {
        try {
            setLoading(true);
            const data = await adminManagementService.getUserDetail(id);
            setDetail(data);
        } catch (err) {
            setError('Failed to load user details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="admin-loading">Loading user details...</div>;
    if (error) return <div className="admin-error">{error}</div>;
    if (!detail || !detail.user) return <div className="admin-error">User not found</div>;

    const { user, testGoals, executionPlans, testExecutions, planExecutions } = detail;

    // A helper to format timestamps safely
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="admin-page admin-user-detail">
            <div className="admin-breadcrumb">
                <Link to="/admin/users">
                    <ChevronLeft size={16} />
                    Back to Users
                </Link>
            </div>

            <div className="admin-detail-header">
                <div className="header-with-icon">
                    <div className="icon-wrapper user-icon">
                        <User size={32} />
                    </div>
                    <div>
                        <h1>{user.fullName || 'No Name'}</h1>
                        <p className="subtitle">ID: {user.id}</p>
                    </div>
                </div>
                <div className="status-badges">
                    {user.isEmailVerified ? (
                        <span className="status-badge success"><CheckCircle size={14} /> Verified Email</span>
                    ) : (
                        <span className="status-badge warning"><XCircle size={14} /> Unverified Email</span>
                    )}
                </div>
            </div>

            <div className="detail-grid" style={{ marginBottom: '2rem' }}>
                <div className="detail-card">
                    <div className="card-header">
                        <Mail size={20} />
                        <h3>Contact Information</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Email:</span>
                            <span className="info-value">{user.email}</span>
                        </div>
                    </div>
                </div>

                <div className="detail-card">
                    <div className="card-header">
                        <Calendar size={20} />
                        <h3>Account Activity</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Joined:</span>
                            <span className="info-value">{formatDate(user.createdAt)}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Last Login:</span>
                            <span className="info-value">{formatDate(user.lastLoginAt)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Tabs Navigation */}
            <div className="admin-tabs">
                <button
                    className={`tab-btn ${activeTab === 'goals' ? 'active' : ''}`}
                    onClick={() => setActiveTab('goals')}
                >
                    Test Goals ({testGoals?.length || 0})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
                    onClick={() => setActiveTab('plans')}
                >
                    Execution Plans ({executionPlans?.length || 0})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'testExecutions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('testExecutions')}
                >
                    Test Executions ({testExecutions?.length || 0})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'planExecutions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('planExecutions')}
                >
                    Plan Executions ({planExecutions?.length || 0})
                </button>
            </div>

            {/* Tab Contents */}
            <div className="admin-tab-content" style={{ marginTop: '1rem' }}>

                {/* Test Goals Tab */}
                {activeTab === 'goals' && (
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Goal</th>
                                    <th>Workspace</th>
                                    <th>Platform</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testGoals && testGoals.length > 0 ? (
                                    testGoals.map(goal => (
                                        <tr key={goal.id}>
                                            <td style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={goal.goalText || 'Untitled'}>
                                                {goal.goalText || 'Untitled'}
                                            </td>
                                            <td>{goal.workspace?.name || 'Unknown'}</td>
                                            <td><span className={`platform-badge ${goal.platform}`}>{goal.platform}</span></td>
                                            <td><span className={`status-badge ${goal.status === 'success' ? 'success' : 'info'}`}>{goal.status}</span></td>
                                            <td>{formatDate(goal.createdAt)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="5" className="empty-state">No Test Goals found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Execution Plans Tab */}
                {activeTab === 'plans' && (
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Plan Name</th>
                                    <th>Workspace</th>
                                    <th>Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {executionPlans && executionPlans.length > 0 ? (
                                    executionPlans.map(plan => (
                                        <tr key={plan.id}>
                                            <td>{plan.name}</td>
                                            <td>{plan.workspace?.name || 'Unknown'}</td>
                                            <td>{formatDate(plan.createdAt)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="3" className="empty-state">No Execution Plans found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Test Executions Tab */}
                {activeTab === 'testExecutions' && (
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Goal</th>
                                    <th>Workspace</th>
                                    <th>Status</th>

                                    <th>Run At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testExecutions && testExecutions.length > 0 ? (
                                    testExecutions.map(exec => (
                                        <tr key={exec.id}>
                                            <td style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={exec.testGoal?.goalText || 'Unknown'}>
                                                {exec.testGoal?.goalText || 'Unknown'}
                                            </td>
                                            <td>{exec.workspace?.name || 'Unknown'}</td>
                                            <td>
                                                <span className={`status-badge ${exec.status === 'success' ? 'success' : (exec.status === 'failed' || exec.status === 'error' ? 'danger' : 'warning')}`}>
                                                    {exec.status}
                                                </span>
                                            </td>

                                            <td>{formatDate(exec.createdAt)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="5" className="empty-state">No Test Executions found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Plan Executions Tab */}
                {activeTab === 'planExecutions' && (
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Plan Name</th>
                                    <th>Workspace</th>
                                    <th>Status</th>
                                    <th>Run At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {planExecutions && planExecutions.length > 0 ? (
                                    planExecutions.map(exec => (
                                        <tr key={exec.id}>
                                            <td>{exec.executionPlan?.name || 'Unknown'}</td>
                                            <td>{exec.workspace?.name || 'Unknown'}</td>
                                            <td>
                                                <span className={`status-badge ${exec.status === 'success' ? 'success' : (exec.status === 'failed' || exec.status === 'error' ? 'danger' : 'warning')}`}>
                                                    {exec.status}
                                                </span>
                                            </td>
                                            <td>{formatDate(exec.createdAt)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="empty-state">No Plan Executions found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

            </div>
        </div>
    );
}

export default AdminUserDetail;
